/* Wildtera — entry motion.
 *
 * CLAUDE.md §4.4 asks for a 500-650ms fade-and-rise on entry and nothing that
 * bounces. The transition itself lives in wildtera-ui.css; this file only
 * decides when an element has arrived.
 *
 * Two things it deliberately does not do:
 *
 *   - It does not hide anything. `.wt-motion` on <html> is what makes reveals
 *     start invisible, and that class is added by a small inline script in the
 *     layout head, before first paint, only when motion is actually wanted. If
 *     this file fails to load the page is fully readable — it just does not
 *     animate. Hiding first and revealing with a deferred script is how a page
 *     ends up blank on a slow connection.
 *
 *   - It does not re-hide on exit. An element that has arrived stays arrived,
 *     so scrolling back up a page does not replay the whole composition.
 */
(function () {
  'use strict';

  var root = document.documentElement;

  if (!root.classList.contains('wt-motion')) return;

  var observer = null;

  /* The warp finishes stringing in about 1.8s; after that the drawn layer is
     handed back to the gradient. Purely tidying — the drawn lines are the
     same lines, so if this never runs nothing looks different. */
  if (root.classList.contains('wt-stringing')) {
    window.setTimeout(function () {
      root.classList.remove('wt-stringing');
    }, 2000);
  }

  function reveal(el) {
    el.classList.add('is-in');
    if (observer) observer.unobserve(el);
  }

  function start() {
    var targets = document.querySelectorAll('.wt-reveal, .wt-media--zoom');
    if (!targets.length) return;

    observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) reveal(entry.target);
        });
      },
      {
        /* Fire a little before the element's top edge clears the fold, so the
           rise is finishing as it comes into view rather than starting there. */
        rootMargin: '0px 0px -8% 0px',
        threshold: 0.01,
      }
    );

    targets.forEach(function (el) {
      /* Anything already on screen at load — the hero, the first band — is
         revealed on the next frame rather than through the observer, so the
         first paint is not a page of empty boxes waiting for a callback. */
      var box = el.getBoundingClientRect();
      if (box.top < window.innerHeight && box.bottom > 0) {
        requestAnimationFrame(function () {
          reveal(el);
        });
        return;
      }
      observer.observe(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  function restart() {
    if (observer) observer.disconnect();
    observer = null;
    start();
  }

  /* The piece travels from its card to its page (the view transition rules
     at the end of wildtera-ui.css). Only the clicked card's photograph is
     named, and the gallery's own name is lifted first, so there is never more
     than one `wt-piece` on the page being left. A modified click opens a new
     tab, where there is no transition to name anything for. */
  document.addEventListener('click', function (event) {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    var link = event.target.closest && event.target.closest('.wt-piece-card__link');
    if (!link) return;
    var media = link.querySelector('.wt-media');
    if (!media) return;
    document.querySelectorAll('.wt-gallery__slide .wt-media').forEach(function (el) {
      el.style.viewTransitionName = 'none';
    });
    media.style.viewTransitionName = 'wt-piece';
  });

  /* A maker's plate hands its photograph to their page the same way. */
  document.addEventListener('click', function (event) {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    var link = event.target.closest && event.target.closest('.wt-maker-plate__link, .wt-maker-plate__name-link');
    if (!link) return;
    var plate = link.closest('.wt-maker-plate');
    var media = plate && plate.querySelector('.wt-maker-plate__frame > .wt-media');
    if (!media) return;
    media.style.viewTransitionName = 'wt-maker';
  });

  /* Coming back to this page from the back-forward cache brings the clicked
     card's name with it; clear it so the next click starts clean. */
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    document.querySelectorAll('.wt-media').forEach(function (el) {
      if (el.style.viewTransitionName) el.style.viewTransitionName = '';
    });
  });

  /* Sections added by the theme editor after load need observing too. */
  document.addEventListener('shopify:section:load', restart);

  /* So does markup swapped in without a page load — the filtered shop grid
     from snippets/wt-facets.liquid is the one that does this today. Its tiles
     carry .wt-reveal, which means they arrive at opacity 0 under .wt-motion and
     stay there unless something observes them. */
  document.addEventListener('wildtera:reveal', restart);

  /* A piece's other photographs, on a resting pointer — snippets/
     wt-piece-images.liquid. Delegated from the document, so tiles the facet
     band or the theme editor writes in later work without being found first.
     Mouse and pen only: a touch is a tap, and a tap navigates. */
  var CYCLE_DELAY = 1500;

  function hydrate(cycle) {
    if (cycle.wtFrames) return cycle.wtFrames;
    var frames = [null];
    cycle.querySelectorAll('template[data-wt-cycle-frame]').forEach(function (tpl) {
      var frame = document.importNode(tpl.content, true).querySelector('.wt-cycle__frame');
      if (!frame) return;
      /* Off screen it would have been lazy; under the pointer it is wanted now. */
      frame.querySelectorAll('img').forEach(function (img) {
        img.loading = 'eager';
      });
      cycle.appendChild(frame);
      frames.push(frame);
    });
    cycle.wtFrames = frames;
    cycle.wtIndex = 0;
    return frames;
  }

  function show(cycle, index) {
    var current = cycle.wtIndex || 0;
    if (index === current) return;
    var prev = cycle.wtFrames[current];
    var next = cycle.wtFrames[index];
    cycle.wtIndex = index;
    if (!prev) {
      next.classList.add('is-active');
      return;
    }
    prev.classList.remove('is-active');
    /* Back to the first frame, which is always beneath: let the last one fade. */
    if (!next) return;
    prev.classList.add('is-leaving');
    next.classList.add('is-active');
    window.setTimeout(function () {
      prev.classList.remove('is-leaving');
    }, 700);
  }

  function step(cycle) {
    var index = ((cycle.wtIndex || 0) + 1) % cycle.wtFrames.length;
    /* A maker's plate (snippets/wt-maker-plate.liquid) turns once, from the face
       to the work, and stays there while the pointer rests: the gesture says
       the two are one subject, and flipping back every 1.5s would say nothing. */
    if (index === 0 && cycle.hasAttribute('data-wt-cycle-hold')) return;
    var frame = cycle.wtFrames[index];
    var img = frame && frame.querySelector('img');
    function go() {
      if (!cycle.wtHover) return;
      show(cycle, index);
      cycle.wtTimer = window.setTimeout(function () {
        step(cycle);
      }, CYCLE_DELAY);
    }
    /* Never fade to a frame that has not arrived. */
    if (img && !img.complete) {
      img.addEventListener('load', go, { once: true });
      img.addEventListener('error', go, { once: true });
    } else {
      go();
    }
  }

  function settle(cycle) {
    cycle.wtHover = false;
    window.clearTimeout(cycle.wtTimer);
    if (cycle.wtFrames) show(cycle, 0);
  }

  document.addEventListener('pointerover', function (event) {
    if (event.pointerType === 'touch') return;
    var cycle = event.target.closest && event.target.closest('[data-wt-cycle]');
    if (!cycle || cycle.wtHover) return;
    if (!cycle.wtFrames && !cycle.querySelector('template[data-wt-cycle-frame]')) return;
    if (hydrate(cycle).length < 2) return;
    cycle.wtHover = true;
    window.clearTimeout(cycle.wtTimer);
    cycle.wtTimer = window.setTimeout(function () {
      step(cycle);
    }, CYCLE_DELAY);
  });

  document.addEventListener('pointerout', function (event) {
    var cycle = event.target.closest && event.target.closest('[data-wt-cycle]');
    if (!cycle || !cycle.wtHover) return;
    if (event.relatedTarget && cycle.contains(event.relatedTarget)) return;
    settle(cycle);
  });

  /* Back from the piece's page, every tile starts on its first photograph. */
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    document.querySelectorAll('[data-wt-cycle]').forEach(settle);
  });
})();
